import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageContent, getSettings, subscribeToPage, subscribeToSettings } from '../firebase/firebase-config';
import '../styles/rich-text.css';

function Home() {
  const [pageContent, setPageContent] = useState({
    heroTitle: 'Welcome to MIS International School',
    heroSubtitle: 'Excellence in Education Since 1995',
    aboutTitle: 'About Our School',
    aboutText: '',
    aboutImage: '',
    showAbout: 'yes',
    showMission: 'yes',
    featuresTitle: 'Why Choose Us',
    features: [],
    showFeatures: 'yes',
    noticesTitle: 'Latest Announcements',
    notices: [],
    showNotices: 'yes',
    latestNewsTitle: 'Trending News',
    latestNews: [],
    showLatestNews: 'yes',
    galleryTitle: 'Photo Gallery & Videos',
    showGallery: 'yes',
    showVideos: 'yes',
    statsTitle: 'School at a Glance',
    stats: [],
    showStats: 'yes',
    testimonialsTitle: 'What Parents Say',
    testimonials: [],
    showTestimonials: 'yes',
    collaborationsTitle: 'Collaborations & Associations',
    collaborations: [],
    showCollaborations: 'yes',
    achievementsTitle: 'Our Achievements',
    achievements: [],
    showAchievements: 'yes',
    calendarTitle: 'Upcoming Events',
    showUpcomingEvents: 'yes',
    showLeadership: 'yes',
    ctaTitle: 'Join MIS International School',
    ctaSubtitle: 'Give your child the best education',
    ctaButtonText: 'Apply Now',
    ctaButtonLink: '/admissions',
    ctaBackgroundImage: '',
    showCTA: 'yes'
  });

  const [aboutContent, setAboutContent] = useState({
    mission: '',
    vision: '',
    philosophy: '',
    directorMessage: '', directorName: '', directorPhoto: '',
    principalMessage: '', principalName: '', principalPhoto: '',
    headmistressMessage: '', headmistressName: '', headmistressPhoto: '',
    extraDesks: [],
  });

  const [galleryContent, setGalleryContent] = useState({ photos: [], videos: [] });
  const [calendarContent, setCalendarContent] = useState({ calendarEvents: [] });

  const [settings, setSettings] = useState({
    primaryColor: '#1a3a6b', secondaryColor: '#f59e0b',
    schoolName: 'MIS International School',
    facebook: '', instagram: '', youtube: '', twitter: '',
    globalBannerImages: [], globalBannerVideo: '', useVideoBanner: false
  });

  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [popupNotice, setPopupNotice] = useState(null);
  const [activeNotice, setActiveNotice] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [newsIndex, setNewsIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [homeData, aboutData, galleryData, calData, settingsData] = await Promise.all([
          getPageContent('home'),
          getPageContent('about'),
          getPageContent('gallery'),
          getPageContent('calendar'),
          getSettings()
        ]);
        if (homeData) setPageContent(prev => ({ ...prev, ...homeData }));
        if (aboutData) setAboutContent(prev => ({ ...prev, ...aboutData }));
        if (galleryData) setGalleryContent(prev => ({ ...prev, ...galleryData }));
        if (calData) setCalendarContent(prev => ({ ...prev, ...calData }));
        if (settingsData) setSettings(settingsData);

        if (homeData?.notices?.length > 0) {
          const lastSeen = localStorage.getItem('lastSeenNoticeId');
          const latest = homeData.notices[0];
          if (!lastSeen || latest.id !== lastSeen) { setPopupNotice(latest); setShowPopup(true); }
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();

    const unsubs = [
      subscribeToPage('home', d => { if (d) setPageContent(p => ({ ...p, ...d })); }),
      subscribeToPage('about', d => { if (d) setAboutContent(p => ({ ...p, ...d })); }),
      subscribeToPage('gallery', d => { if (d) setGalleryContent(p => ({ ...p, ...d })); }),
      subscribeToPage('calendar', d => { if (d) setCalendarContent(p => ({ ...p, ...d })); }),
      subscribeToSettings(d => { if (d) setSettings(d); }),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  useEffect(() => {
    const imgs = settings.globalBannerImages;
    if (!imgs || imgs.length <= 1) return;
    const t = setInterval(() => setCurrentBannerIndex(p => (p + 1) % imgs.length), 5000);
    return () => clearInterval(t);
  }, [settings.globalBannerImages?.length]);

  const closePopup = () => {
    setShowPopup(false);
    if (popupNotice) localStorage.setItem('lastSeenNoticeId', popupNotice.id);
  };

  const getYouTubeId = url => {
    const m = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?#]+)/);
    return m ? m[1] : null;
  };

  const formatFull = ds => {
    if (!ds) return '';
    return new Date(ds).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatShort = ds => {
    if (!ds) return '';
    return new Date(ds).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading...</p>
    </div>
  );

  const pc = pageContent;
  const primary = settings.primaryColor || '#1a3a6b';
  const secondary = settings.secondaryColor || '#f59e0b';

  const mission = aboutContent.mission || pc.mission;
  const vision = aboutContent.vision || pc.vision;
  const philosophy = aboutContent.philosophy || pc.philosophy;

  const calEvents = (calendarContent.calendarEvents || [])
    .filter(e => e.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .filter(e => new Date(e.date) >= new Date());

  const galleryPhotos = galleryContent.photos || [];
  const galleryVideos = galleryContent.videos || [];

  const desks = [];
  if (aboutContent.directorMessage || aboutContent.directorName) desks.push({
    label: "DIRECTOR'S", name: aboutContent.directorName, photo: aboutContent.directorPhoto,
    message: aboutContent.directorMessage, role: 'DIRECTOR', badge: primary,
  });
  if (aboutContent.principalMessage || aboutContent.principalName) desks.push({
    label: "PRINCIPAL'S", name: aboutContent.principalName, photo: aboutContent.principalPhoto,
    message: aboutContent.principalMessage, role: 'PRINCIPAL', badge: '#7c3aed',
  });
  if (aboutContent.headmistressMessage || aboutContent.headmistressName) desks.push({
    label: "HEADMISTRESS'S", name: aboutContent.headmistressName, photo: aboutContent.headmistressPhoto,
    message: aboutContent.headmistressMessage, role: 'HEADMISTRESS', badge: '#0891b2',
  });
  (aboutContent.extraDesks || []).forEach(d => {
    if (d.message || d.name) desks.push({
      label: `${(d.role || 'STAFF').toUpperCase()}'S`, name: d.name, photo: d.photo,
      message: d.message, role: d.role || 'STAFF', badge: d.color || '#475569',
    });
  });

  const newsList = pc.latestNews || [];
  const maxNI = Math.max(0, newsList.length - 1);

  const allGalleryItems = [
    ...galleryPhotos.map(p => ({ type: 'photo', ...p })),
    ...galleryVideos.map(v => ({ type: 'video', ...v }))
  ];

  const NoticeModal = ({ notice, onClose }) => notice ? (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-box" onClick={e => e.stopPropagation()}>
        <div className="popup-header" style={{ background: primary }}>
          <span>📢</span> {notice.title}
          <button className="popup-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="popup-body">
          {notice.image && <img src={notice.image} alt={notice.title} className="popup-img" />}
          <div 
            className="rich-text-content"
            dangerouslySetInnerHTML={{ __html: notice.content || '' }}
          />
          <div className="popup-date">🕐 {formatFull(notice.date)}</div>
        </div>
        <div className="popup-footer">
          <button onClick={onClose} style={{ background: primary }}>Close</button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="home-page">

      {!isMobile && (
        settings.useVideoBanner && settings.globalBannerVideo ? (
          <div className="global-banner video-banner">
            <video autoPlay muted loop playsInline className="banner-video">
              <source src={settings.globalBannerVideo} type="video/mp4" />
            </video>
          </div>
        ) : settings.globalBannerImages?.length > 0 ? (
          <div className="global-banner image-slider">
            {settings.globalBannerImages.map((img, i) => (
              <div key={i} className={`slider-slide ${i === currentBannerIndex ? 'active' : ''}`}
                style={{ backgroundImage: `url(${img.url})` }} />
            ))}
            <button className="sl-btn sl-prev" onClick={() => setCurrentBannerIndex(p => (p - 1 + settings.globalBannerImages.length) % settings.globalBannerImages.length)}>❮</button>
            <button className="sl-btn sl-next" onClick={() => setCurrentBannerIndex(p => (p + 1) % settings.globalBannerImages.length)}>❯</button>
            <div className="slider-dots">
              {settings.globalBannerImages.map((_, i) => (
                <span key={i} className={`dot ${i === currentBannerIndex ? 'active' : ''}`} onClick={() => setCurrentBannerIndex(i)} />
              ))}
            </div>
          </div>
        ) : (
          <div className="global-banner default-banner" style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}>
            <div><h1>{settings.schoolName}</h1><p>Excellence in Education</p></div>
          </div>
        )
      )}

      {showPopup && popupNotice && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-box" onClick={e => e.stopPropagation()}>
            <div className="popup-header" style={{ background: primary }}>
              <span>📢</span> Notice
              <button className="popup-close-btn" onClick={closePopup}>×</button>
            </div>
            <div className="popup-body">
              <h3>{popupNotice.title}</h3>
              {popupNotice.image && <img src={popupNotice.image} alt={popupNotice.title} className="popup-img" />}
              <div 
                className="rich-text-content"
                dangerouslySetInnerHTML={{ __html: popupNotice.content || '' }}
              />
              <div className="popup-date">🕐 {formatFull(popupNotice.date)}</div>
            </div>
            <div className="popup-footer">
              <button onClick={closePopup} style={{ background: primary }}>Got it</button>
            </div>
          </div>
        </div>
      )}

      <NoticeModal notice={activeNotice} onClose={() => setActiveNotice(null)} />

      {/* Mission, Vision, Philosophy Section - Using dangerouslySetInnerHTML */}
      {pc.showMission !== 'no' && (mission || vision || philosophy) && (
        <section className="sec">
          <div className="container">
            <div className="mvp-grid">
              {mission && (
                <div className="mvp-card" style={{ borderTopColor: primary }}>
                  <h3>Our Mission</h3>
                  <div className="divider" style={{ background: secondary }} />
                  <div 
                    className="mvp-content rich-text-content"
                    dangerouslySetInnerHTML={{ __html: mission }}
                  />
                </div>
              )}
              {vision && (
                <div className="mvp-card" style={{ borderTopColor: secondary }}>
                  <h3>Our Vision</h3>
                  <div className="divider" style={{ background: secondary }} />
                  <div 
                    className="mvp-content rich-text-content"
                    dangerouslySetInnerHTML={{ __html: vision }}
                  />
                </div>
              )}
              {philosophy && (
                <div className="mvp-card" style={{ borderTopColor: primary }}>
                  <h3>Our Philosophy</h3>
                  <div className="divider" style={{ background: secondary }} />
                  <div 
                    className="mvp-content rich-text-content"
                    dangerouslySetInnerHTML={{ __html: philosophy }}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Leadership Section - Using dangerouslySetInnerHTML */}
      {pc.showLeadership !== 'no' && desks.length > 0 && (
        <section className="sec leadership-section">
          <div className="container">
            <h2 className="sec-title" style={{ color: primary }}>Leadership Insights</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="leader-grid">
              {desks.map((desk, idx) => (
                <div key={idx} className="leader-card">
                  {desk.photo && (
                    <div className="leader-image-wrapper">
                      <img src={desk.photo} alt={desk.name || desk.role} className="leader-image" />
                      <div className="leader-badge" style={{ background: desk.badge }}>
                        <strong>{desk.name || desk.role}</strong>
                        <span>{desk.role}</span>
                      </div>
                    </div>
                  )}
                  <div className="leader-content">
                    <p className="desk-label">
                      FROM THE <span style={{ color: secondary }}>{desk.label}</span> DESK
                    </p>
                    <div className="leader-message rich-text-content">
                      <div dangerouslySetInnerHTML={{ __html: desk.message || '' }} />
                    </div>
                    <Link to="/about" className="read-more-btn" style={{ borderColor: primary, color: primary }}>
                      Read More →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section - Using dangerouslySetInnerHTML */}
      {pc.showAbout !== 'no' && (pc.aboutText || pc.aboutImage) && (
        <section className="sec">
          <div className="container">
            <div className="about-grid">
              <div className="about-content">
                <h2>{pc.aboutTitle}</h2>
                <div className="divider" style={{ background: secondary }} />
                <div 
                  className="rich-text-content"
                  dangerouslySetInnerHTML={{ __html: pc.aboutText }}
                />
                <Link to="/about" className="read-more-btn" style={{ borderColor: primary, color: primary }}>Read More →</Link>
              </div>
              {pc.aboutImage && (
                <div className="about-image">
                  <img src={pc.aboutImage} alt="About" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {pc.showFeatures !== 'no' && pc.features?.length > 0 && (
        <section className="sec features-section">
          <div className="container">
            <h2 className="sec-title">{pc.featuresTitle}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="features-grid">
              {pc.features.map((f, i) => (
                <div key={i} className="feature-card">
                  <div className="feature-icon">{f.icon || '📚'}</div>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats Section */}
      {pc.showStats !== 'no' && pc.stats?.length > 0 && (
        <section className="sec stats-section" style={{ background: primary }}>
          <div className="container">
            <h2 className="sec-title" style={{ color: '#fff' }}>{pc.statsTitle}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="stats-grid">
              {pc.stats.map((s, i) => (
                <div key={i} className="stat-card">
                  <div className="stat-number">{s.number}</div>
                  <div className="stat-label">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events Section */}
      {pc.showUpcomingEvents !== 'no' && calEvents.length > 0 && (
        <section className="sec events-section">
          <div className="container">
            <h2 className="sec-title">{pc.calendarTitle || 'Upcoming Events'}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="events-grid">
              {calEvents.slice(0, 6).map((ev, i) => {
                const d = new Date(ev.date);
                return (
                  <div key={i} className="event-card">
                    <div className="event-date" style={{ background: secondary }}>
                      <span className="event-day">{d.getDate()}</span>
                      <span className="event-month">{d.toLocaleString('default', { month: 'short' })}</span>
                      <span className="event-year">{d.getFullYear()}</span>
                    </div>
                    <div className="event-details">
                      <h4>{ev.event || ev.title}</h4>
                      {ev.description && (
                        <div 
                          className="rich-text-content"
                          dangerouslySetInnerHTML={{ __html: ev.description }}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Notices Section - Using dangerouslySetInnerHTML */}
      {pc.showNotices !== 'no' && pc.notices?.length > 0 && (
        <section className="sec notices-section">
          <div className="container">
            <h2 className="sec-title">{pc.noticesTitle}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="notices-grid">
              {pc.notices.slice(0, 3).map((n, i) => (
                <div key={i} className="notice-card" style={{ borderTopColor: n.isOld ? '#94a3b8' : secondary }}
                  onClick={() => setActiveNotice(n)}>
                  <div className="notice-date" style={{ background: n.isOld ? '#94a3b8' : secondary }}>
                    {n.date ? formatShort(n.date) : 'New'}
                  </div>
                  {n.image && (
                    <div className="notice-image-wrapper">
                      <img src={n.image} alt={n.title} className="notice-image" />
                    </div>
                  )}
                  <div className="notice-content">
                    <h3>{n.title}</h3>
                    <div 
                      className="rich-text-content"
                      dangerouslySetInnerHTML={{ __html: n.content }}
                    />
                    <span className="read-more-link">Click to read more →</span>
                  </div>
                </div>
              ))}
            </div>
            {pc.notices.length > 3 && (
              <div className="view-all-container">
                <Link to="/notices" className="view-all-btn" style={{ background: primary }}>View All Notices →</Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Trending News Section - Using dangerouslySetInnerHTML */}
      {pc.showLatestNews !== 'no' && newsList.length > 0 && (
        <section className="sec trending-section">
          <div className="container">
            <h2 className="sec-title">
              {pc.latestNewsTitle || 'Trending News'}
              <Link to="/notices" className="trending-link" style={{ color: secondary }}> | View All</Link>
            </h2>
            <div className="trending-news-box">
              <div className="trending-news-content">
                {newsList[newsIndex]?.image && (
                  <div className="trending-news-image">
                    <img src={newsList[newsIndex].image} alt="news" onClick={() => setSelectedImage(newsList[newsIndex].image)} />
                  </div>
                )}
                <div className="trending-news-text">
                  <div 
                    className="rich-text-content"
                    dangerouslySetInnerHTML={{ __html: newsList[newsIndex]?.content || '' }}
                  />
                  <div className="trending-news-date">🕐 {formatFull(newsList[newsIndex]?.date)}</div>
                </div>
              </div>
              <div className="trending-news-nav">
                <button onClick={() => setNewsIndex(p => Math.max(0, p - 1))}
                  disabled={newsIndex === 0} className="nav-btn"
                  style={{ background: newsIndex === 0 ? '#cbd5e1' : primary }}>‹</button>
                <span>{newsIndex + 1} / {newsList.length}</span>
                <button onClick={() => setNewsIndex(p => Math.min(maxNI, p + 1))}
                  disabled={newsIndex === maxNI} className="nav-btn"
                  style={{ background: newsIndex === maxNI ? '#cbd5e1' : primary }}>›</button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Achievements Section - Using dangerouslySetInnerHTML */}
      {pc.showAchievements !== 'no' && pc.achievements?.length > 0 && (
        <section className="sec achievements-section">
          <div className="container">
            <h2 className="sec-title">{pc.achievementsTitle}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="achievements-grid">
              {pc.achievements.map((a, i) => (
                <div key={i} className="achievement-card">
                  <span className="achievement-year" style={{ background: secondary }}>{a.year}</span>
                  <h4>{a.title}</h4>
                  <div 
                    className="rich-text-content"
                    dangerouslySetInnerHTML={{ __html: a.description || '' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section - Using dangerouslySetInnerHTML */}
      {pc.showTestimonials !== 'no' && pc.testimonials?.length > 0 && (
        <section className="sec testimonials-section">
          <div className="container">
            <h2 className="sec-title">{pc.testimonialsTitle}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="testimonials-grid">
              {pc.testimonials.slice(0, 3).map((t, i) => (
                <div key={i} className="testimonial-card">
                  <div className="testimonial-quote" style={{ color: secondary }}>"</div>
                  <div 
                    className="testimonial-text rich-text-content"
                    dangerouslySetInnerHTML={{ __html: t.text }}
                  />
                  <div className="testimonial-author">
                    {t.image && <img src={t.image} alt={t.name} />}
                    <div>
                      <strong>{t.name}</strong>
                      <span>{t.designation}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {(pc.showGallery !== 'no' || pc.showVideos !== 'no') && allGalleryItems.length > 0 && (
        <section className="sec gallery-section">
          <div className="container">
            <h2 className="sec-title">{pc.galleryTitle || 'Photo Gallery & Videos'}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="gallery-grid">
              {allGalleryItems.slice(0, 6).map((item, i) => (
                <div key={i} className="gallery-card">
                  {item.type === 'photo' ? (
                    <div className="gallery-photo" onClick={() => setSelectedImage(item.url)}>
                      <img src={item.url} alt={item.title || 'Gallery'} />
                      <div className="gallery-overlay">
                        {item.title && <h4>{item.title}</h4>}
                        {item.caption && <p>{item.caption}</p>}
                      </div>
                    </div>
                  ) : (
                    <div className="gallery-video">
                      {(() => {
                        const videoId = getYouTubeId(item.url);
                        return videoId ? (
                          <iframe src={`https://www.youtube.com/embed/${videoId}`} title={item.title} frameBorder="0" allowFullScreen />
                        ) : (
                          <div className="video-placeholder">🎬 Video unavailable</div>
                        );
                      })()}
                      {item.title && <h4 className="video-title">{item.title}</h4>}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {allGalleryItems.length > 6 && (
              <div className="view-all-container">
                <Link to="/gallery" className="view-all-btn" style={{ background: primary }}>View Full Gallery →</Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Collaborations Section */}
      {pc.showCollaborations !== 'no' && pc.collaborations?.length > 0 && (
        <section className="sec collaborations-section">
          <div className="container">
            <h2 className="sec-title">{pc.collaborationsTitle}</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="collaborations-grid">
              {pc.collaborations.map((c, i) => (
                <div key={i} className="collaboration-card">
                  {c.logo ? (
                    c.website ? (
                      <a href={c.website} target="_blank" rel="noopener noreferrer">
                        <img src={c.logo} alt={c.name} />
                      </a>
                    ) : (
                      <img src={c.logo} alt={c.name} />
                    )
                  ) : (
                    <div className="collaboration-placeholder">🤝</div>
                  )}
                  <span>{c.name}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Social Media Section */}
      {(settings.facebook || settings.instagram || settings.youtube || settings.twitter) && (
        <section className="sec social-section">
          <div className="container">
            <h2 className="sec-title">Follow Us</h2>
            <div className="cdivider" style={{ background: secondary }} />
            <div className="social-grid">
              {settings.facebook && <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="social-btn facebook">📘 Facebook</a>}
              {settings.instagram && <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="social-btn instagram">📷 Instagram</a>}
              {settings.youtube && <a href={settings.youtube} target="_blank" rel="noopener noreferrer" className="social-btn youtube">▶ YouTube</a>}
              {settings.twitter && <a href={settings.twitter} target="_blank" rel="noopener noreferrer" className="social-btn twitter">𝕏 Twitter</a>}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {pc.showCTA !== 'no' && pc.ctaTitle && (
        <section className="cta-section" style={{ background: secondary, backgroundImage: pc.ctaBackgroundImage ? `url(${pc.ctaBackgroundImage})` : 'none' }}>
          <div className="cta-overlay" style={{ background: pc.ctaBackgroundImage ? 'rgba(0,0,0,0.6)' : 'none' }}>
            <div className="container">
              <h2>{pc.ctaTitle}</h2>
              {pc.ctaSubtitle && (
                <div 
                  className="rich-text-content"
                  dangerouslySetInnerHTML={{ __html: pc.ctaSubtitle }}
                />
              )}
              <Link to={pc.ctaButtonLink} className="cta-button" style={{ background: primary }}>{pc.ctaButtonText}</Link>
            </div>
          </div>
        </section>
      )}

      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)}>
          <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
          <img src={selectedImage} alt="Full size" />
        </div>
      )}

      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .home-page {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          overflow-x: hidden;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: ${primary};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 60px 24px;
        }

        .sec {
          width: 100%;
        }

        .sec-title {
          text-align: center;
          font-size: 36px;
          font-weight: 700;
          margin: 0 0 12px;
          color: ${primary};
        }

        .eyebrow {
          text-align: center;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 2px;
          margin-bottom: 8px;
        }

        .divider {
          width: 60px;
          height: 4px;
          border-radius: 2px;
          margin: 0 auto 32px;
        }

        .cdivider {
          width: 60px;
          height: 4px;
          border-radius: 2px;
          margin: 0 auto 40px;
        }

        /* Banner Styles */
        .global-banner {
          position: relative;
          width: 100%;
          height: 550px;
          overflow: hidden;
        }

        .image-slider {
          height: 100%;
        }

        .slider-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 0.8s ease;
        }

        .slider-slide.active {
          opacity: 1;
        }

        .banner-video {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .default-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
        }

        .default-banner h1 {
          color: #fff;
          font-size: 48px;
          margin-bottom: 12px;
        }

        .default-banner p {
          color: rgba(255,255,255,0.9);
          font-size: 18px;
        }

        .sl-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0,0,0,0.5);
          color: #fff;
          border: none;
          padding: 12px 18px;
          cursor: pointer;
          font-size: 18px;
          z-index: 10;
          border-radius: 50%;
          transition: background 0.3s;
        }

        .sl-btn:hover {
          background: rgba(0,0,0,0.8);
        }

        .sl-prev { left: 20px; }
        .sl-next { right: 20px; }

        .slider-dots {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 10px;
          z-index: 10;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255,255,255,0.5);
          cursor: pointer;
          transition: background 0.3s;
        }

        .dot.active {
          background: #fff;
        }

        /* Popup Modal */
        .popup-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .popup-box {
          background: #fff;
          border-radius: 16px;
          width: 100%;
          max-width: 450px;
          max-height: 85vh;
          overflow-y: auto;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          animation: slideIn 0.25s ease;
        }

        @keyframes slideIn {
          from { transform: translateY(-30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .popup-header {
          position: relative;
          color: #fff;
          padding: 16px 45px 16px 20px;
          border-radius: 16px 16px 0 0;
          font-weight: 700;
          font-size: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .popup-close-btn {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 24px;
          background: none;
          border: none;
          cursor: pointer;
          color: #fff;
        }

        .popup-body {
          padding: 20px;
        }

        .popup-body h3 {
          color: #1e293b;
          margin-bottom: 12px;
        }

        .popup-img {
          width: 100%;
          max-height: 200px;
          object-fit: contain;
          background: #f5f5f5;
          border-radius: 8px;
          margin-bottom: 16px;
        }

        .popup-date {
          font-size: 12px;
          color: #94a3b8;
          margin-top: 8px;
        }

        .popup-footer {
          padding: 16px 20px 20px;
        }

        .popup-footer button {
          color: #fff;
          border: none;
          padding: 10px 24px;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          width: 100%;
        }

        /* MVP Grid */
        .mvp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 32px;
        }

        .mvp-card {
          background: #f8fafc;
          border-radius: 20px;
          padding: 32px 28px;
          border-top: 4px solid;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          transition: transform 0.3s;
        }

        .mvp-card:hover {
          transform: translateY(-5px);
        }

        .mvp-card h3 {
          font-size: 26px;
          margin-bottom: 20px;
          text-align: center;
        }

        /* About Section */
        .about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 50px;
          align-items: center;
        }

        .about-content h2 {
          font-size: 32px;
          color: ${primary};
          margin-bottom: 16px;
        }

        .about-image img {
          width: 100%;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
        }

        /* Features Grid */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 30px;
        }

        .feature-card {
          text-align: center;
          padding: 32px 24px;
          background: #fff;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .feature-card:hover {
          transform: translateY(-5px);
        }

        .feature-icon {
          font-size: 48px;
          margin-bottom: 20px;
        }

        .feature-card h3 {
          color: ${primary};
          margin-bottom: 12px;
          font-size: 18px;
        }

        .feature-card p {
          color: #64748b;
          line-height: 1.6;
          font-size: 14px;
        }

        /* Stats Section */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 40px;
          text-align: center;
        }

        .stat-number {
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 12px;
        }

        .stat-label {
          font-size: 16px;
          opacity: 0.9;
        }

        /* Events Grid */
        .events-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 25px;
        }

        .event-card {
          display: flex;
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .event-card:hover {
          transform: translateY(-3px);
        }

        .event-date {
          min-width: 85px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #fff;
          padding: 16px 12px;
          text-align: center;
        }

        .event-day {
          font-size: 28px;
          font-weight: 800;
          line-height: 1;
        }

        .event-month {
          font-size: 12px;
          text-transform: uppercase;
          font-weight: 600;
          margin-top: 4px;
        }

        .event-year {
          font-size: 10px;
          opacity: 0.85;
          margin-top: 2px;
        }

        .event-details {
          padding: 16px;
          flex: 1;
        }

        .event-details h4 {
          color: ${primary};
          font-size: 16px;
          margin-bottom: 8px;
        }

        /* Notices Grid */
        .notices-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 28px;
        }

        .notice-card {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          border-top: 4px solid;
          cursor: pointer;
          transition: transform 0.3s, box-shadow 0.3s;
        }

        .notice-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 30px rgba(0,0,0,0.12);
        }

        .notice-date {
          padding: 10px 18px;
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          display: inline-block;
        }

        .notice-image-wrapper {
          width: 100%;
          height: 180px;
          overflow: hidden;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .notice-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .notice-content {
          padding: 20px;
        }

        .notice-content h3 {
          color: ${primary};
          font-size: 17px;
          margin-bottom: 10px;
        }

        .read-more-link {
          display: inline-block;
          margin-top: 12px;
          font-size: 12px;
          color: ${secondary};
          font-weight: 600;
        }

        /* Trending News */
        .trending-news-box {
          background: #fff;
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.08);
        }

        .trending-news-content {
          display: flex;
          gap: 32px;
          align-items: flex-start;
        }

        .trending-news-image {
          flex-shrink: 0;
          width: 260px;
          height: 180px;
          background: #f5f5f5;
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .trending-news-image img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }

        .trending-news-text {
          flex: 1;
        }

        .trending-news-date {
          color: #64748b;
          font-size: 13px;
          margin-top: 16px;
        }

        .trending-news-nav {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 16px;
          margin-top: 24px;
        }

        .nav-btn {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: none;
          color: #fff;
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: opacity 0.3s;
        }

        .nav-btn:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .trending-link {
          text-decoration: none;
          font-weight: 400;
          font-size: 17px;
        }

        /* Achievements Grid */
        .achievements-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 24px;
        }

        .achievement-card {
          background: #fff;
          padding: 24px;
          border-radius: 16px;
          text-align: center;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .achievement-card:hover {
          transform: translateY(-3px);
        }

        .achievement-year {
          display: inline-block;
          color: #fff;
          padding: 4px 16px;
          border-radius: 25px;
          font-size: 12px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .achievement-card h4 {
          color: ${primary};
          font-size: 16px;
          margin-bottom: 8px;
        }

        /* Testimonials Grid */
        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 32px;
        }

        .testimonial-card {
          background: #fff;
          padding: 32px;
          border-radius: 20px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          position: relative;
          transition: transform 0.3s;
        }

        .testimonial-card:hover {
          transform: translateY(-3px);
        }

        .testimonial-quote {
          font-size: 60px;
          opacity: 0.2;
          position: absolute;
          top: 20px;
          left: 25px;
          font-family: serif;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 15px;
          padding-top: 16px;
          border-top: 1px solid #f1f5f9;
        }

        .testimonial-author img {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          object-fit: cover;
        }

        .testimonial-author strong {
          color: ${primary};
          font-size: 14px;
          display: block;
        }

        .testimonial-author span {
          color: #64748b;
          font-size: 12px;
        }

        /* Gallery Grid */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px;
        }

        .gallery-card {
          background: #fff;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .gallery-card:hover {
          transform: translateY(-5px);
        }

        .gallery-photo {
          position: relative;
          cursor: pointer;
          height: 220px;
          overflow: hidden;
        }

        .gallery-photo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s;
        }

        .gallery-photo:hover img {
          transform: scale(1.05);
        }

        .gallery-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0,0,0,0.8));
          padding: 16px;
          color: #fff;
          transform: translateY(100%);
          transition: transform 0.3s;
        }

        .gallery-photo:hover .gallery-overlay {
          transform: translateY(0);
        }

        .gallery-overlay h4 {
          font-size: 14px;
          margin-bottom: 4px;
        }

        .gallery-overlay p {
          font-size: 12px;
          opacity: 0.9;
        }

        .gallery-video iframe {
          width: 100%;
          height: 220px;
          border: none;
        }

        .video-title {
          padding: 14px;
          margin: 0;
          font-size: 14px;
          color: ${primary};
          font-weight: 600;
        }

        .video-placeholder {
          height: 220px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f1f5f9;
          color: #94a3b8;
        }

        /* Collaborations Grid */
        .collaborations-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 30px;
          text-align: center;
        }

        .collaboration-card {
          background: #fff;
          padding: 24px 16px;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          transition: transform 0.3s;
        }

        .collaboration-card:hover {
          transform: translateY(-3px);
        }

        .collaboration-card img {
          max-width: 80px;
          height: auto;
          margin-bottom: 12px;
        }

        .collaboration-placeholder {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .collaboration-card span {
          display: block;
          font-size: 13px;
          color: #475569;
          font-weight: 500;
        }

        /* Social Media */
        .social-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 16px;
        }

        .social-btn {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 28px;
          border-radius: 40px;
          color: #fff;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: transform 0.3s, filter 0.3s;
        }

        .social-btn:hover {
          transform: translateY(-2px);
          filter: brightness(0.9);
        }

        .social-btn.facebook { background: #1877f2; }
        .social-btn.instagram { background: #e4405f; }
        .social-btn.youtube { background: #ff0000; }
        .social-btn.twitter { background: #1da1f2; }

        /* CTA Section */
        .cta-section {
          position: relative;
          background-size: cover;
          background-position: center;
        }

        .cta-overlay {
          padding: 80px 20px;
          text-align: center;
        }

        .cta-overlay h2 {
          color: #fff;
          font-size: 38px;
          margin-bottom: 16px;
        }

        .cta-button {
          display: inline-block;
          padding: 14px 42px;
          border: none;
          border-radius: 50px;
          font-size: 16px;
          font-weight: 700;
          color: #fff;
          text-decoration: none;
          transition: transform 0.3s;
        }

        .cta-button:hover {
          transform: translateY(-2px);
        }

        /* View All */
        .view-all-container {
          text-align: center;
          margin-top: 40px;
        }

        .view-all-btn {
          display: inline-block;
          color: #fff;
          padding: 12px 32px;
          border-radius: 40px;
          text-decoration: none;
          font-weight: 600;
          font-size: 14px;
          transition: transform 0.3s, background 0.3s;
        }

        .view-all-btn:hover {
          transform: translateY(-2px);
          background: ${secondary} !important;
        }

        /* Lightbox */
        .lightbox {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }

        .lightbox img {
          max-width: 90%;
          max-height: 90%;
          object-fit: contain;
        }

        .lightbox-close {
          position: absolute;
          top: 20px;
          right: 30px;
          font-size: 40px;
          color: #fff;
          background: none;
          border: none;
          cursor: pointer;
        }

        /* Leadership Section */
        .leader-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: 32px;
          align-items: stretch;
        }

        .leader-card {
          background: #fff;
          border-radius: 28px;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .leader-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 55px rgba(0,0,0,0.16);
        }

        .leader-image-wrapper {
          position: relative;
          width: 100%;
          height: 340px;
          background: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .leader-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          background: #000;
        }

        .leader-badge {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 18px 16px;
          text-align: center;
          backdrop-filter: blur(4px);
        }

        .leader-badge strong {
          color: #fff;
          font-size: 24px;
          display: block;
          margin-bottom: 6px;
          font-weight: 700;
          text-transform: capitalize;
        }

        .leader-badge span {
          color: rgba(255,255,255,0.95);
          font-size: 14px;
          letter-spacing: 2px;
          text-transform: uppercase;
        }

        .leader-content {
          padding: 30px;
          display: flex;
          flex-direction: column;
          flex: 1;
        }

        .desk-label {
          font-size: 15px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: 1px;
          text-transform: uppercase;
          margin-bottom: 18px;
          line-height: 1.6;
        }

        .leader-message {
          flex: 1;
        }

        .read-more-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-top: 24px;
          padding: 14px 30px;
          border: 2px solid;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 700;
          font-size: 14px;
          transition: all 0.3s ease;
          width: fit-content;
        }

        .read-more-btn:hover {
          background: ${primary};
          color: #fff !important;
          transform: translateY(-2px);
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .container {
            padding: 50px 20px;
          }
          .sec-title {
            font-size: 32px;
          }
          .leader-grid {
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          }
          .leader-image-wrapper {
            height: 300px;
          }
        }

        @media (max-width: 768px) {
          .container {
            padding: 40px 16px;
          }
          
          .sec-title {
            font-size: 28px;
          }
          
          .cdivider, .divider {
            margin-bottom: 30px;
          }
          
          .mvp-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .mvp-card {
            padding: 24px;
          }
          
          .mvp-card h3 {
            font-size: 22px;
          }
          
          .about-grid {
            grid-template-columns: 1fr;
            gap: 32px;
          }
          
          .about-content h2 {
            font-size: 28px;
          }
          
          .leader-grid {
            grid-template-columns: 1fr;
            gap: 26px;
          }
          
          .leader-image-wrapper {
            height: 320px;
          }
          
          .leader-badge {
            padding: 16px 12px;
          }
          
          .leader-badge strong {
            font-size: 20px;
          }
          
          .leader-content {
            padding: 24px 22px;
            text-align: center;
          }
          
          .desk-label {
            font-size: 14px;
            text-align: center;
          }
          
          .read-more-btn {
            width: 100%;
            margin-top: 22px;
          }
          
          .features-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 30px;
          }
          
          .stat-number {
            font-size: 36px;
          }
          
          .events-grid {
            grid-template-columns: 1fr;
          }
          
          .notices-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .notice-image-wrapper {
            height: 160px;
          }
          
          .trending-news-content {
            flex-direction: column;
          }
          
          .trending-news-image {
            width: 100%;
            height: 200px;
          }
          
          .achievements-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .testimonials-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .gallery-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }
          
          .gallery-photo, .gallery-video iframe {
            height: 200px;
          }
          
          .collaborations-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 16px;
          }
          
          .social-grid {
            flex-direction: column;
            align-items: center;
          }
          
          .social-btn {
            width: 100%;
            justify-content: center;
          }
          
          .cta-overlay {
            padding: 50px 20px;
          }
          
          .cta-overlay h2 {
            font-size: 28px;
          }
          
          .cta-overlay p {
            font-size: 16px;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 32px 16px;
          }
          
          .sec-title {
            font-size: 24px;
          }
          
          .leader-image-wrapper {
            height: 280px;
          }
          
          .leader-content {
            padding: 20px 16px;
          }
          
          .stats-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }
          
          .collaborations-grid {
            grid-template-columns: 1fr;
          }
          
          .trending-news-image {
            height: 160px;
          }
          
          .notice-image-wrapper {
            height: 140px;
          }
          
          .gallery-photo, .gallery-video iframe {
            height: 180px;
          }
        }
      `}</style>
    </div>
  );
}

export default Home;